import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { Autocomplete } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDModal from "components/MDModel";
import FormField from "layouts/applications/wizard/components/FormField";
import MDDatePicker from "components/MDDatePicker";
import MDTypography from "components/MDTypography";
import crudsService from "services/cruds-service";

const GenerateContactModel = ({ company_id, onSave }) => {
  const [contactLogs, setContactLogs] = useState(null);
  const [contactLogsList, setContactLogsList] = useState([]);
  const [eventType, setEventType] = useState(null);
  const [eventTypeList] = useState([
    { label: "Campaign Email", value: "campaign_email" },
    { label: "Phone", value: "phone" },
  ]);

  const [contactMade, setContactMade] = useState(false);
  const [unknownContact, setUnknownContact] = useState(false);
  const [voicemailDate, setVoicemailDate] = useState(null);
  const [entryTimestamp, setEntryTimestamp] = useState(null);

  const [eventNotes, setEventNotes] = useState({
    text: "",
    error: false,
    textError: "",
  });
  const [spokeName, setSpokeName] = useState({
    text: "",
    error: false,
    textError: "",
  });

  const [openContactModel, setOpenContactModel] = useState(false);

  useEffect(() => {
    if (openContactModel) {
      setContactLogsList(
        company_id?.CampaignContacts?.map((contact) => ({
          label: `${contact.first_name} ${contact.last_name}`,
          value: contact.contact_id,
        })) || []
      );
    }
  }, [openContactModel, company_id]);

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!company_id.company_id) {
      alert("Please Select Records.");
      return;
    }

    if (!contactLogs || !eventType) {
      alert("Please fill in required fields.");
      return;
    }

    const data = {
      company_id: company_id.company_id,
      campaign_id: localStorage.getItem("selectedCampaign"),
      contact_logs: contactLogs.value,
      event_type: eventType.value,
      contact_made: contactMade,
      unknown_contact: unknownContact,
      ...(voicemailDate ? { voicemail_timestamp: voicemailDate } : {}),
      ...(entryTimestamp ? { entry_timestamp: entryTimestamp } : {}),
      event_notes: eventNotes.text.trim(),
      spoke_name: spokeName.text.trim(),
    };

    try {
      let response = await crudsService.generateCampaignContact(data);
      onSave();
      setOpenContactModel(false);

      setContactLogs(null);
      setEventType(null);
      setContactMade(false);
      setUnknownContact(false);
      setVoicemailDate(null);
      setEntryTimestamp(null);
      setEventNotes({ text: "", error: false, textError: "" });
      setSpokeName({ text: "", error: false, textError: "" });
    } catch (err) {
      console.error("Submission Error:", err);
    }
  };

  return (
    <>
      <MDButton variant="contained" onClick={() => setOpenContactModel(true)}>
        Generate Event
      </MDButton>

      <MDModal
        open={openContactModel}
        onClose={() => setOpenContactModel(false)}
        footer={
          <MDButton variant="gradient" color="dark" onClick={submitHandler}>
            Save
          </MDButton>
        }
      >
        <Grid container justifyContent="center" spacing={2}>
          <Grid item xs={12} lg={12}>
            <MDBox px={3} my={2}>
              <Autocomplete
                sx={{ marginTop: "0.5rem" }}
                options={contactLogsList}
                getOptionLabel={(option) => option.label ?? ""}
                value={contactLogs}
                onChange={(event, newValue) => setContactLogs(newValue)}
                renderInput={(params) => (
                  <FormField {...params} label="Contact Logs" required />
                )}
              />

              <Autocomplete
                sx={{ marginTop: "0.5rem" }}
                options={eventTypeList}
                getOptionLabel={(option) => option.label ?? ""}
                value={eventType}
                onChange={(event, newValue) => setEventType(newValue)}
                renderInput={(params) => (
                  <FormField {...params} label="Event Type" required />
                )}
              />

              <FormField
                type="text"
                label="Event Notes"
                value={eventNotes.text}
                error={eventNotes.error}
                onKeyDown={(e) => e.stopPropagation()}
                onChange={(e) =>
                  setEventNotes({ ...eventNotes, text: e.target.value })
                }
              />
              {eventNotes.error && (
                <MDTypography variant="caption" color="error">
                  {eventNotes.textError}
                </MDTypography>
              )}

              <FormField
                type="text"
                label="Spoke Name"
                value={spokeName.text}
                error={spokeName.error}
                onKeyDown={(e) => e.stopPropagation()}
                onChange={(e) =>
                  setSpokeName({ ...spokeName, text: e.target.value })
                }
              />
              {spokeName.error && (
                <MDTypography variant="caption" color="error">
                  {spokeName.textError}
                </MDTypography>
              )}
              <MDBox display="flex" mt={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={contactMade}
                      onChange={(e) => setContactMade(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Contact Made"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={unknownContact}
                      onChange={(e) => setUnknownContact(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Unknown Contact"
                />
              </MDBox>

              <MDBox display="flex" gap="16px" spax mt={2}>
                <MDDatePicker
                  value={voicemailDate}
                  onChange={(date) => setVoicemailDate(date[0])}
                  options={{
                    dateFormat: "Y-m-d H:i",
                    enableTime: true,
                    minDate: new Date(),
                  }}
                  input={{ placeholder: "Voicemail Timestamp" }}
                />

                <MDDatePicker
                  value={entryTimestamp}
                  onChange={(date) => setEntryTimestamp(date[0])}
                  options={{
                    dateFormat: "Y-m-d H:i",
                    enableTime: true,
                    minDate: new Date(),
                  }}
                  input={{ placeholder: "Entry Timestamp" }}
                />
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </MDModal>
    </>
  );
};

export default GenerateContactModel;
